// src/lib/invoiceSequence.ts
// ─────────────────────────────────────────────────────────────────────────────
// SECUENCIA ÚNICA DE FACTURAS: FAC-0001, FAC-0002…
//
// · Una sola nomenclatura corta para POS, restaurante, hotel, alquiler,
//   suscripciones, convenios, venta rápida y cualquier plataforma.
// · El número se ata a la resolución DIAN activa del negocio (prefijo, rango
//   y consecutivo). Si no hay resolución, se usa FAC y rango local.
// · El contador se cachea en localStorage para asignar de forma síncrona desde
//   los hooks de Dexie (así ninguna venta se queda sin factura).
// ─────────────────────────────────────────────────────────────────────────────

import { INVOICE_CONFIG } from '@/config/constants';

export interface InvoiceSequenceConfig {
  businessId: string;
  prefix: string;
  padding: number;
  last: number;
  resolutionId?: string;
  toNumber: number;
  updatedAt: string;
}

export interface ReservedInvoiceNumber {
  number: string;
  raw: number;
  prefix: string;
  resolutionId?: string;
  exhausted: boolean;
}

const cache = new Map<string, InvoiceSequenceConfig>();
const storageKey = (businessId: string): string => `niiz_invoice_sequence_${businessId}`;

export const normalizeInvoicePrefix = (prefix: string): string => {
  const clean = String(prefix || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  return clean || INVOICE_CONFIG.defaultPrefix;
};

export const formatInvoiceNumber = (
  prefix: string,
  number: number,
  padding: number = INVOICE_CONFIG.paddingLength,
): string => {
  const safeNumber = Math.max(1, Math.floor(Number(number) || 1));
  return `${normalizeInvoicePrefix(prefix)}-${String(safeNumber).padStart(
    Math.max(1, padding),
    '0',
  )}`;
};

const readStored = (businessId: string): InvoiceSequenceConfig | null => {
  const cached = cache.get(businessId);
  if (cached) return cached;
  try {
    const raw = localStorage.getItem(storageKey(businessId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as Record<string, unknown>;
    const config: InvoiceSequenceConfig = {
      businessId,
      prefix: normalizeInvoicePrefix(String(record['prefix'] || '')),
      padding: Math.max(1, Number(record['padding']) || INVOICE_CONFIG.paddingLength),
      last: Math.max(0, Number(record['last']) || 0),
      ...(typeof record['resolutionId'] === 'string'
        ? { resolutionId: record['resolutionId'] }
        : {}),
      toNumber: Math.max(0, Number(record['toNumber']) || 0),
      updatedAt: String(record['updatedAt'] || new Date().toISOString()),
    };
    cache.set(businessId, config);
    return config;
  } catch {
    return null;
  }
};

const persist = (config: InvoiceSequenceConfig): void => {
  cache.set(config.businessId, config);
  try {
    localStorage.setItem(storageKey(config.businessId), JSON.stringify(config));
  } catch {
    /* almacenamiento no disponible */
  }
};

/** Inicializa/reconcilia la secuencia con la resolución DIAN y las ventas. */
export const initInvoiceSequence = (config: InvoiceSequenceConfig): InvoiceSequenceConfig => {
  const existing = readStored(config.businessId);
  const merged: InvoiceSequenceConfig = {
    ...config,
    prefix: normalizeInvoicePrefix(config.prefix),
    padding: Math.max(1, config.padding || INVOICE_CONFIG.paddingLength),
    last: Math.max(config.last, existing?.last ?? 0),
    updatedAt: new Date().toISOString(),
  };
  persist(merged);
  return merged;
};

const ensureConfig = (businessId: string): InvoiceSequenceConfig => {
  const existing = readStored(businessId);
  if (existing) return existing;
  const fallback: InvoiceSequenceConfig = {
    businessId,
    prefix: INVOICE_CONFIG.defaultPrefix,
    padding: INVOICE_CONFIG.paddingLength,
    last: 0,
    toNumber: 0,
    updatedAt: new Date().toISOString(),
  };
  persist(fallback);
  return fallback;
};

/**
 * Reserva el siguiente número. Es síncrono para poder usarse dentro de los
 * hooks `creating`/`updating` de Dexie sin dejar ventas sin factura.
 */
export const reserveInvoiceNumber = (businessId: string): ReservedInvoiceNumber => {
  const config = ensureConfig(businessId);
  const raw = config.last + 1;
  const next: InvoiceSequenceConfig = {
    ...config,
    last: raw,
    updatedAt: new Date().toISOString(),
  };
  persist(next);
  return {
    number: formatInvoiceNumber(config.prefix, raw, config.padding),
    raw,
    prefix: config.prefix,
    ...(config.resolutionId ? { resolutionId: config.resolutionId } : {}),
    exhausted: config.toNumber > 0 && raw > config.toNumber,
  };
};

/** Lee la configuración actual sin reservar. */
export const peekInvoiceSequence = (businessId: string): InvoiceSequenceConfig =>
  ensureConfig(businessId);

/** Extrae el consecutivo de un número ya formateado (FAC-0001 → 1). */
export const invoiceNumberValue = (invoiceNumber?: string | null): number => {
  if (!invoiceNumber) return 0;
  const match = /(\d+)\s*$/.exec(invoiceNumber);
  return match?.[1] ? Number(match[1]) : 0;
};
